import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../api/client";
import { theme, card, floatingSheet } from "../theme";
import { FREE_REFERENCE_FRAMEWORKS, hasPaidStandard, withAlternative, withoutOrphanedReferenceFrameworks, type FrameworkId } from "../context/FrameworksContext";
import { COUNTRIES, ALL_REGULATIONS, regulationsForCountries } from "../data/countriesData";
import { CI_SECTORS, CI_SECTOR_PARENTS, subSectors } from "../data/sectorsData";
import { allowsFreeText, toggleWithExclusivity } from "../lib/intakeOptions";
import { BugReportButton } from "../components/BugReportButton";

// ── Constants ─────────────────────────────────────────────────────────────

// ISO 14001 and IWA 48 are not offered here: the Environment & Sustainability
// Governance add-on (My Services) is what pays for them, and switching it on is what
// enrols them (backend lib/frameworks.ts).
const FRAMEWORK_ORDER: FrameworkId[] = [
  "iso27001", "cyber_essentials", "cyber_essentials_plus",
  "iso42001", "iso9001", "soc2", "pci_dss",
  "iasme_ca_sole_trader", "iasme_ca_micro", "iasme_ca_small", "iasme_ca_medium_large", "nist_csf",
  "iso45001", "ncsc_caf",
];

const FRAMEWORK_LABELS: Record<FrameworkId, string> = {
  iso27001:              "ISO 27001:2022",
  cyber_essentials:      "Cyber Essentials",
  cyber_essentials_plus: "Cyber Essentials Plus",
  iso42001:              "ISO 42001:2023",
  iso9001:               "ISO 9001:2015",
  soc2:                  "SOC 2",
  pci_dss:               "PCI DSS v4.0",
  iasme_ca_sole_trader:  "IASME Cyber Assurance — Sole Trader",
  iasme_ca_micro:        "IASME Cyber Assurance — Micro",
  iasme_ca_small:        "IASME Cyber Assurance — Small",
  iasme_ca_medium_large: "IASME Cyber Assurance — Medium/Large",
  nist_csf:              "NIST Cybersecurity Framework 2.0",
  gdpr:                  "GDPR",
  uk_gdpr:               "UK GDPR",
  iso45001:              "ISO 45001:2018",
  iso14001:              "ISO 14001:2015",
  iwa48:                 "IWA 48:2024",
  ncsc_caf:              "NCSC Cyber Assessment Framework v4.0",
};

const FRAMEWORK_DESCRIPTIONS: Record<FrameworkId, string> = {
  iso27001:              "Information security management",
  cyber_essentials:      "UK government-backed cyber security",
  cyber_essentials_plus: "Cyber Essentials with independent assessment",
  iso42001:              "Artificial intelligence management",
  iso9001:               "Quality management system",
  soc2:                  "Service organisation controls",
  pci_dss:               "Payment card industry data security",
  iasme_ca_sole_trader:  "IASME Cyber Assurance for sole traders & partnerships (1–2 people)",
  iasme_ca_micro:        "IASME Cyber Assurance for micro businesses (3–9 people)",
  iasme_ca_small:        "IASME Cyber Assurance for small businesses (10–49 people)",
  iasme_ca_medium_large: "IASME Cyber Assurance for medium & large businesses (50+ people)",
  nist_csf:              "Free alongside any standard — US government guidance for managing cybersecurity risk across all sectors; no certification workflow",
  gdpr:                  "EU General Data Protection Regulation — personal data processing obligations",
  uk_gdpr:               "UK-retained GDPR post-Brexit — applies to organisations processing UK residents' data",
  iso45001:              "Occupational Health & Safety Management System — protect workers and demonstrate legal compliance",
  iso14001:              "Environmental Management System — identify environmental aspects, set targets, and demonstrate regulatory compliance",
  iwa48:                 "ESG Implementation Principles — embed environmental, social and governance practices across your organisation",
  ncsc_caf:              "NCSC Cyber Assessment Framework — the outcome-based assessment operators of essential services are measured against (UK NIS, and the natural yardstick for other OES regimes)",
};

// ── Types ─────────────────────────────────────────────────────────────────

interface Question {
  id: string;
  frameworkId: string | null;
  regulation: string | null;
  section: string;
  questionText: string;
  helpText: string | null;
  sortOrder: number;
  orgProfileField: string | null;
  questionType: "TEXT" | "SINGLE_CHOICE" | "MULTI_CHOICE";
  options: string | null;
}

interface OrgProfile {
  name: string;
  sector: string | null;
  size: string | null;
  ceo: string | null;
  ciso: string | null;
  dpo: string | null;
  itManager: string | null;
  hrLead: string | null;
  chiefAiOfficer: string | null;
  qualityManager: string | null;
  ismsScope: string | null;
  aimsScope: string | null;
  qmsScope: string | null;
  socServiceDescription: string | null;
}

// ── Helpers ────────────────────────────────────────────────────────────────

function parseOptions(raw: string | null): string[] {
  if (!raw) return [];
  try { return JSON.parse(raw) as string[]; } catch { return []; }
}

function hasRealAnswer(text: string): boolean {
  if (!text.trim()) return false;
  const lines = text.split("\n").filter(Boolean);
  return lines.some((l) => l !== "Other: " && (l.startsWith("Other: ") ? l.slice(7).trim().length > 0 : true));
}

function parseJsonArray(raw: string): string[] {
  if (!raw.trim()) return [];
  try { return JSON.parse(raw) as string[]; } catch { return []; }
}

function isYesNoOptions(opts: string[]): boolean {
  return opts.length === 2 && opts[0] === "Yes" && opts[1] === "No";
}

// ── Regulated sector picker (onboarding) ───────────────────────────────────
//
// Stores the sector id, which syncs to Organization.sectorCode and decides which cyber-security
// laws designate the organisation. Answering it can reveal further questions, so the caller
// treats it as a scope change and refetches.

function OnboardingSectorPicker({ value, onChange }: { value: string; onChange: (text: string) => void }) {
  const description = value ? CI_SECTORS.find((s) => s.id === value)?.description : null;
  return (
    <div>
      <select value={value} onChange={(e) => onChange(e.target.value)} style={{ ...inp, fontSize: 12, padding: "6px 9px" }}>
        <option value="">— select a sector —</option>
        {CI_SECTOR_PARENTS.map((parent) => {
          const children = subSectors(parent.id);
          return children.length === 0 ? (
            <option key={parent.id} value={parent.id}>{parent.label}</option>
          ) : (
            <optgroup key={parent.id} label={parent.label}>
              <option value={parent.id}>{parent.label} (all)</option>
              {children.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </optgroup>
          );
        })}
      </select>
      {description && (
        <div style={{ fontSize: 11, color: theme.color.faint, marginTop: 4, lineHeight: 1.5 }}>{description}</div>
      )}
    </div>
  );
}

// ── Compact country picker (onboarding) ────────────────────────────────────

function OnboardingCountryPicker({ value, onChange }: { value: string; onChange: (text: string) => void }) {
  const selected = parseJsonArray(value);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const filtered = COUNTRIES.filter((c) => c.toLowerCase().includes(search.toLowerCase()) && !selected.includes(c));

  function add(c: string) {
    const next = [...selected, c];
    onChange(JSON.stringify(next));
    setSearch("");
  }
  function remove(c: string) {
    const next = selected.filter((x) => x !== c);
    onChange(next.length ? JSON.stringify(next) : "");
  }

  useEffect(() => {
    function h(e: MouseEvent) { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); }
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  return (
    <div ref={ref}>
      {selected.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 6 }}>
          {selected.map((c) => (
            <span key={c} style={{ display: "inline-flex", alignItems: "center", gap: 4, background: `${theme.color.accent}18`, color: theme.color.accentText, border: `1px solid ${theme.color.accent}40`, borderRadius: 3, padding: "2px 7px", fontSize: 11, fontWeight: 500 }}>
              {c}
              <button onClick={() => remove(c)} style={{ background: "none", border: "none", cursor: "pointer", color: theme.color.accentText, padding: 0, lineHeight: 1, fontSize: 12 }}>×</button>
            </span>
          ))}
        </div>
      )}
      <div style={{ position: "relative" }}>
        <input type="text" value={search} onChange={(e) => { setSearch(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)}
          placeholder={selected.length ? "Add another country…" : "Search countries…"}
          style={{ ...inp, fontSize: 12, padding: "6px 9px" }} />
        {open && filtered.length > 0 && (
          <div style={{ position: "absolute", zIndex: 50, top: "100%", left: 0, right: 0, marginTop: 3, ...floatingSheet, maxHeight: 180, overflowY: "auto" }}>
            {filtered.slice(0, 40).map((c) => (
              <button key={c} onMouseDown={(e) => { e.preventDefault(); add(c); }}
                style={{ display: "block", width: "100%", textAlign: "left", padding: "6px 10px", background: "none", border: "none", fontSize: 12, color: theme.color.text, cursor: "pointer", fontFamily: "inherit" }}
                onMouseEnter={(e) => { (e.target as HTMLButtonElement).style.background = theme.color.border; }}
                onMouseLeave={(e) => { (e.target as HTMLButtonElement).style.background = "none"; }}>
                {c}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Compact regulations picker (onboarding) ────────────────────────────────

function OnboardingRegulationsPicker({ value, geoValue, onChange }: { value: string; geoValue: string; onChange: (text: string) => void }) {
  const geoCountries = parseJsonArray(geoValue);
  const autoRegs = regulationsForCountries(geoCountries);
  const autoSet = new Set(autoRegs);
  const [manualRegs, setManualRegs] = useState<string[]>(() => parseJsonArray(value).filter((r) => !autoSet.has(r)));
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const allSelected = new Set([...autoRegs, ...manualRegs]);
  const filtered = ALL_REGULATIONS.filter((r) => r.toLowerCase().includes(search.toLowerCase()) && !allSelected.has(r));

  function sync(extras: string[]) {
    const combined = [...autoRegs, ...extras];
    onChange(combined.length ? JSON.stringify(combined) : "");
  }

  // Re-sync when geography changes; write back only when the combined list actually differs.
  useEffect(() => {
    const newAuto = regulationsForCountries(geoCountries);
    const newSet = new Set(newAuto);
    const keep = manualRegs.filter((r) => !newSet.has(r));
    if (keep.length !== manualRegs.length) setManualRegs(keep);
    const combined = [...newAuto, ...keep];
    const text = combined.length ? JSON.stringify(combined) : "";
    if (text !== value) onChange(text);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geoValue]);

  function addReg(r: string) { const next = [...manualRegs, r]; setManualRegs(next); sync(next); setSearch(""); setOpen(false); }
  function removeReg(r: string) { const next = manualRegs.filter((x) => x !== r); setManualRegs(next); sync(next); }

  useEffect(() => {
    function h(e: MouseEvent) { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); }
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  return (
    <div ref={ref}>
      {autoRegs.length > 0 && (
        <div style={{ marginBottom: 6 }}>
          <div style={{ fontSize: 10, color: theme.color.muted, marginBottom: 4 }}>Auto-derived:</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
            {autoRegs.map((r) => (
              <span key={r} style={{ background: theme.color.border, color: theme.color.text, borderRadius: 3, padding: "2px 7px", fontSize: 10, fontWeight: 500 }}>{r}</span>
            ))}
          </div>
        </div>
      )}
      {manualRegs.length > 0 && (
        <div style={{ marginBottom: 6 }}>
          <div style={{ fontSize: 10, color: theme.color.muted, marginBottom: 4 }}>Manually added:</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
            {manualRegs.map((r) => (
              <span key={r} style={{ display: "inline-flex", alignItems: "center", gap: 4, background: `${theme.color.accent}18`, color: theme.color.accentText, border: `1px solid ${theme.color.accent}40`, borderRadius: 3, padding: "2px 7px", fontSize: 10, fontWeight: 500 }}>
                {r}<button onClick={() => removeReg(r)} style={{ background: "none", border: "none", cursor: "pointer", color: theme.color.accentText, padding: 0, lineHeight: 1, fontSize: 11 }}>×</button>
              </span>
            ))}
          </div>
        </div>
      )}
      <div style={{ position: "relative" }}>
        <input type="text" value={search} onChange={(e) => { setSearch(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)}
          placeholder="Search to add a regulation…"
          style={{ ...inp, fontSize: 12, padding: "6px 9px" }} />
        {open && filtered.length > 0 && (
          <div style={{ position: "absolute", zIndex: 50, top: "100%", left: 0, right: 0, marginTop: 3, ...floatingSheet, maxHeight: 160, overflowY: "auto" }}>
            {filtered.slice(0, 30).map((r) => (
              <button key={r} onMouseDown={(e) => { e.preventDefault(); addReg(r); }}
                style={{ display: "block", width: "100%", textAlign: "left", padding: "5px 10px", background: "none", border: "none", fontSize: 11, color: theme.color.text, cursor: "pointer", fontFamily: "inherit" }}
                onMouseEnter={(e) => { (e.target as HTMLButtonElement).style.background = theme.color.border; }}
                onMouseLeave={(e) => { (e.target as HTMLButtonElement).style.background = "none"; }}>
                {r}
              </button>
            ))}
          </div>
        )}
      </div>
      {autoRegs.length === 0 && manualRegs.length === 0 && (
        <div style={{ fontSize: 10, color: theme.color.faint, marginTop: 3 }}>Select countries above to auto-populate, or search to add manually.</div>
      )}
    </div>
  );
}

// ── Inline question input (compact, for onboarding wizard) ─────────────────

function OnboardingQuestionInput({
  q,
  value,
  onChange,
  allAnswers,
  allQuestions,
}: {
  q: Question;
  value: string;
  onChange: (text: string) => void;
  allAnswers?: Record<string, string>;
  allQuestions?: Question[];
}) {
  const options = parseOptions(q.options);

  if (q.orgProfileField === "geography") {
    return <OnboardingCountryPicker value={value} onChange={onChange} />;
  }

  if (q.orgProfileField === "regulations") {
    const geoQ = allQuestions?.find((x) => x.orgProfileField === "geography");
    const geoValue = geoQ && allAnswers ? (allAnswers[geoQ.id] ?? "") : "";
    return <OnboardingRegulationsPicker value={value} geoValue={geoValue} onChange={onChange} />;
  }

  // The answer is the sector id; the grouped list mirrors My Organisation and the intake page.
  if (q.orgProfileField === "sectorCode") {
    return <OnboardingSectorPicker value={value} onChange={onChange} />;
  }

  if (q.questionType === "SINGLE_CHOICE" && isYesNoOptions(options)) {
    const isYes = value === "Yes" || value.startsWith("Yes\nDetails: ");
    const isNo = value === "No";
    const detailMatch = value.match(/^Yes\nDetails: ([\s\S]*)$/);
    const [detail, setDetail] = useState(detailMatch ? detailMatch[1] : "");

    function pick(v: "Yes" | "No") {
      if (v === "No") { onChange("No"); }
      else { onChange(detail ? `Yes\nDetails: ${detail}` : "Yes"); }
    }
    function handleDetail(val: string) {
      setDetail(val);
      onChange(val ? `Yes\nDetails: ${val}` : "Yes");
    }

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <div style={{ display: "flex", gap: 12 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
            <input type="radio" name={q.id} checked={isYes} onChange={() => pick("Yes")} style={{ accentColor: theme.color.accent }} />
            <span style={{ fontSize: 12, color: theme.color.text }}>Yes</span>
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
            <input type="radio" name={q.id} checked={isNo} onChange={() => pick("No")} style={{ accentColor: theme.color.accent }} />
            <span style={{ fontSize: 12, color: theme.color.text }}>No</span>
          </label>
        </div>
        {isYes && (
          <textarea value={detail} onChange={(e) => handleDetail(e.target.value)} placeholder="Describe the device(s)…" rows={2}
            style={{ ...inp, fontSize: 12, padding: "6px 9px", resize: "vertical", minHeight: 50 }} />
        )}
      </div>
    );
  }

  if (q.questionType === "SINGLE_CHOICE") {
    const freeText = allowsFreeText(q, options);
    let selected = "";
    let otherText = "";
    if (value.startsWith("Other: ")) { selected = "Other"; otherText = value.slice(7); }
    else if (options.includes(value)) { selected = value; }
    else if (value) { selected = "Other"; otherText = value; }

    function pick(opt: string) {
      onChange(opt === "Other" ? (otherText ? `Other: ${otherText}` : "") : opt);
    }

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
        {options.map((opt) => (
          <label key={opt} style={{ display: "flex", alignItems: "center", gap: 7, cursor: "pointer" }}>
            <input type="radio" name={q.id} checked={selected === opt} onChange={() => pick(opt)}
              style={{ accentColor: theme.color.accent, cursor: "pointer", flexShrink: 0 }} />
            <span style={{ fontSize: 12, color: theme.color.text }}>{opt}</span>
          </label>
        ))}
        {freeText && (
          <label style={{ display: "flex", alignItems: "center", gap: 7, cursor: "pointer" }}>
            <input type="radio" name={q.id} checked={selected === "Other"} onChange={() => pick("Other")}
              style={{ accentColor: theme.color.accent, cursor: "pointer", flexShrink: 0 }} />
            <span style={{ fontSize: 12, color: theme.color.text }}>Other</span>
          </label>
        )}
        {freeText && selected === "Other" && (
          <input type="text" value={otherText} placeholder="Please specify…"
            onChange={(e) => onChange(`Other: ${e.target.value}`)}
            style={{ ...inp, fontSize: 12, padding: "6px 9px", marginLeft: 20, width: "calc(100% - 20px)", boxSizing: "border-box" }} />
        )}
      </div>
    );
  }

  if (q.questionType === "MULTI_CHOICE") {
    const freeText = allowsFreeText(q, options);
    const lines = value ? value.split("\n").filter(Boolean) : [];
    const otherLine = lines.find((l) => l.startsWith("Other: "));
    const otherText = otherLine ? otherLine.slice(7) : "";
    const otherSelected = !!otherLine;
    const selectedSet = new Set(lines.filter((l) => !l.startsWith("Other: ")));

    function build(sel: Set<string>, other: string) {
      return [...sel, ...(other ? [`Other: ${other}`] : [])].join("\n");
    }
    function toggle(opt: string) {
      onChange(build(toggleWithExclusivity(selectedSet, opt), otherText));
    }
    function toggleOther() {
      onChange(otherSelected ? build(selectedSet, "") : build(selectedSet, " ").replace("Other:  ", "Other: "));
    }

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
        {options.map((opt) => (
          <label key={opt} style={{ display: "flex", alignItems: "center", gap: 7, cursor: "pointer" }}>
            <input type="checkbox" checked={selectedSet.has(opt)} onChange={() => toggle(opt)}
              style={{ accentColor: theme.color.accent, cursor: "pointer", flexShrink: 0, width: 13, height: 13 }} />
            <span style={{ fontSize: 12, color: theme.color.text }}>{opt}</span>
          </label>
        ))}
        {freeText && (
          <label style={{ display: "flex", alignItems: "center", gap: 7, cursor: "pointer" }}>
            <input type="checkbox" checked={otherSelected} onChange={toggleOther}
              style={{ accentColor: theme.color.accent, cursor: "pointer", flexShrink: 0, width: 13, height: 13 }} />
            <span style={{ fontSize: 12, color: theme.color.text }}>Other</span>
          </label>
        )}
        {freeText && otherSelected && (
          <input type="text" value={otherText} placeholder="Please specify…"
            onChange={(e) => onChange(build(selectedSet, e.target.value))}
            style={{ ...inp, fontSize: 12, padding: "6px 9px", marginLeft: 20, width: "calc(100% - 20px)", boxSizing: "border-box" }} />
        )}
      </div>
    );
  }

  return (
    <textarea style={ta} value={value} onChange={(e) => onChange(e.target.value)} placeholder="Type your answer…" rows={2} />
  );
}

// ── Styles ────────────────────────────────────────────────────────────────

const inp: React.CSSProperties = {
  width: "100%",
  padding: "9px 11px",
  borderRadius: 3,
  border: `1px solid ${theme.color.border}`,
  background: theme.color.panel,
  color: theme.color.text,
  fontSize: 13,
  outline: "none",
  boxSizing: "border-box",
  fontFamily: theme.font,
};

const ta: React.CSSProperties = {
  ...inp,
  resize: "vertical",
  minHeight: 80,
};

const label: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  color: theme.color.muted,
  display: "block",
  marginBottom: 5,
};

const fieldWrap: React.CSSProperties = { marginBottom: 16 };

// ── Step indicator ────────────────────────────────────────────────────────

const STEPS = ["Standards", "Intake questions", "My organisation"];

function StepBar({ current }: { current: number }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 0, marginBottom: 32 }}>
      {STEPS.map((name, i) => {
        const done    = i < current;
        const active  = i === current;
        return (
          <div key={i} style={{ display: "flex", alignItems: "center", flex: i < STEPS.length - 1 ? 1 : undefined }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
              <div style={{
                width: 28, height: 28, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 12, fontWeight: 700,
                background: done ? theme.color.accent : active ? theme.color.accent : theme.color.border,
                color: done || active ? "#fff" : theme.color.muted,
                transition: "background 0.2s",
              }}>
                {done ? "✓" : i + 1}
              </div>
              <div style={{ fontSize: 11, color: active ? theme.color.accent : theme.color.muted, whiteSpace: "nowrap", fontWeight: active ? 600 : 400 }}>
                {name}
              </div>
            </div>
            {i < STEPS.length - 1 && (
              <div style={{ flex: 1, height: 2, background: done ? theme.color.accent : theme.color.border, margin: "0 8px", marginBottom: 20, transition: "background 0.2s" }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Step 1: Standards ─────────────────────────────────────────────────────

function StepStandards({
  selected,
  onToggle,
  onContinue,
  saving,
}: {
  selected: Set<FrameworkId>;
  onToggle: (id: FrameworkId) => void;
  onContinue: () => void;
  saving: boolean;
}) {
  const paidSelected = hasPaidStandard(selected);
  return (
    <div>
      <div style={{ fontSize: 18, fontWeight: 700, color: theme.color.heading, marginBottom: 4 }}>
        Which standards are you working towards?
      </div>
      <div style={{ fontSize: 13, color: theme.color.muted, marginBottom: 24 }}>
        Select all that apply. You can change this later in My Services.
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 28 }}>
        {FRAMEWORK_ORDER.map((id) => {
          const on = selected.has(id);
          // A free reference framework (NIST CSF) rides along with a paid standard.
          const needsStandard = FREE_REFERENCE_FRAMEWORKS.has(id) && !paidSelected;
          return (
            <button
              key={id}
              onClick={() => !needsStandard && onToggle(id)}
              disabled={needsStandard}
              title={needsStandard ? "Free alongside any standard — select a standard first" : undefined}
              style={{
                padding: "12px 14px",
                borderRadius: 3,
                border: `1.5px solid ${on ? theme.color.accent : theme.color.border}`,
                background: on ? `${theme.color.accent}12` : theme.color.panel,
                cursor: needsStandard ? "not-allowed" : "pointer",
                opacity: needsStandard ? 0.5 : 1,
                textAlign: "left",
                transition: "border-color 0.15s, background 0.15s, opacity 0.15s",
              }}
            >
              <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                <div style={{
                  width: 16, height: 16, borderRadius: 4, flexShrink: 0, marginTop: 1,
                  border: `2px solid ${on ? theme.color.accent : theme.color.muted}`,
                  background: on ? theme.color.accent : "transparent",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  {on && <span style={{ color: "#fff", fontSize: 10, lineHeight: 1 }}>✓</span>}
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading }}>{FRAMEWORK_LABELS[id]}</div>
                  <div style={{ fontSize: 11, color: theme.color.muted, marginTop: 2 }}>{FRAMEWORK_DESCRIPTIONS[id]}</div>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <button
        onClick={onContinue}
        disabled={!paidSelected || saving}
        style={{
          width: "100%", padding: "11px 18px", borderRadius: 3, border: "none",
          background: !paidSelected ? theme.color.border : theme.color.accent,
          color: !paidSelected ? theme.color.muted : "#fff",
          fontSize: 13, fontWeight: 600, cursor: !paidSelected ? "not-allowed" : "pointer",
        }}
      >
        {saving ? "Saving…" : "Continue"}
      </button>
    </div>
  );
}

// ── Step 2: Intake questions ──────────────────────────────────────────────

function StepQuestions({
  questions,
  answers,
  onAnswer,
  onContinue,
}: {
  questions: Question[];
  answers: Record<string, string>;
  onAnswer: (questionId: string, text: string) => void;
  onContinue: () => void;
}) {
  // Group by framework, then section, in the order the API returns them. Sections such as
  // "Audit & Performance" exist in several standards; grouping by section alone merged them.
  const groups: Array<{ fw: string; sections: Array<{ section: string; items: Question[] }> }> = [];
  for (const q of questions) {
    const fw = q.frameworkId ?? (q.regulation ? `law:${q.regulation}` : "__general__");
    let group = groups.find((g) => g.fw === fw);
    if (!group) { group = { fw, sections: [] }; groups.push(group); }
    let sec = group.sections.find((x) => x.section === q.section);
    if (!sec) { sec = { section: q.section, items: [] }; group.sections.push(sec); }
    sec.items.push(q);
  }
  // Law question sets keyed by a framework ID sit with the other applicable laws.
  const isLawGroup = (fw: string) => fw.startsWith("law:") || ["gdpr", "uk_gdpr", "jersey_dpjl"].includes(fw);
  const groupLabel = (fw: string) =>
    fw.startsWith("law:") ? fw.slice(4)
      : fw === "__general__" ? "General"
      : fw === "jersey_dpjl" ? "Data Protection (Jersey) Law 2018"
      : (FRAMEWORK_LABELS[fw as FrameworkId] ?? fw);
  const answered = Object.entries(answers).filter(([, v]) => hasRealAnswer(v)).length;

  return (
    <div>
      <div style={{ fontSize: 18, fontWeight: 700, color: theme.color.heading, marginBottom: 4 }}>
        Intake questions
      </div>
      <div style={{ fontSize: 13, color: theme.color.muted, marginBottom: 6 }}>
        Help us understand your organisation. Answers save automatically and pre-fill your profile.
      </div>
      {questions.length > 0 && (
        <div style={{ fontSize: 12, color: theme.color.muted, marginBottom: 20 }}>
          {answered} of {questions.length} answered — you can complete the rest later.
        </div>
      )}

      {questions.length === 0 ? (
        <div style={{ padding: "24px 0", color: theme.color.muted, fontSize: 13, textAlign: "center" }}>
          No questions available for the selected standards yet.
        </div>
      ) : (
        <div style={{ maxHeight: 420, overflowY: "auto", paddingRight: 4, marginBottom: 24 }}>
          {groups.map((group, i) => (
            <div key={group.fw} style={{ marginBottom: 32 }}>
              {isLawGroup(group.fw) && !isLawGroup(groups[i - 1]?.fw ?? "") && (
                <div style={{ borderTop: `1px solid ${theme.color.border}`, paddingTop: 16, marginBottom: 16 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading }}>Applicable laws</div>
                  <div style={{ fontSize: 11, color: theme.color.muted, marginTop: 2 }}>From the countries you operate in and any regulations you added.</div>
                </div>
              )}
              <div style={{ fontSize: 11, fontWeight: 700, color: theme.color.faint, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 12 }}>
                {groupLabel(group.fw)}
              </div>
              {group.sections.map(({ section, items }) => (
                <div key={section} style={{ marginBottom: 24 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: theme.color.accent, marginBottom: 12 }}>
                    {section}
                  </div>
                  {items.map((q) => (
                    <div key={q.id} style={fieldWrap}>
                      <label style={label}>{q.questionText}</label>
                      {q.helpText && (
                        <div style={{ fontSize: 11, color: theme.color.muted, marginBottom: 6, lineHeight: 1.5 }}>{q.helpText}</div>
                      )}
                      <OnboardingQuestionInput q={q} value={answers[q.id] ?? ""} onChange={(text) => onAnswer(q.id, text)} allAnswers={answers} allQuestions={questions} />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      <button
        onClick={onContinue}
        style={{
          width: "100%", padding: "11px 18px", borderRadius: 3, border: "none",
          background: theme.color.accent, color: "#fff",
          fontSize: 13, fontWeight: 600, cursor: "pointer",
        }}
      >
        Continue
      </button>
    </div>
  );
}

// ── Step 3: My organisation ───────────────────────────────────────────────

function StepOrgProfile({
  profile,
  enrolled,
  onChange,
  onComplete,
  saving,
  error,
}: {
  profile: OrgProfile;
  enrolled: Set<FrameworkId>;
  onChange: (field: keyof OrgProfile, value: string) => void;
  onComplete: () => void;
  saving: boolean;
  error: string;
}) {
  const show = (ids: FrameworkId[]) => ids.some((id) => enrolled.has(id));

  function Field({ field, title, multiline, rows }: { field: keyof OrgProfile; title: string; multiline?: boolean; rows?: number }) {
    return (
      <div style={fieldWrap}>
        <label style={label}>{title}</label>
        {multiline ? (
          <textarea style={{ ...ta, minHeight: (rows ?? 3) * 24 }} value={profile[field] ?? ""} onChange={(e) => onChange(field, e.target.value)} />
        ) : (
          <input style={inp} value={profile[field] ?? ""} onChange={(e) => onChange(field, e.target.value)} />
        )}
      </div>
    );
  }

  return (
    <div>
      <div style={{ fontSize: 18, fontWeight: 700, color: theme.color.heading, marginBottom: 4 }}>
        Your organisation
      </div>
      <div style={{ fontSize: 13, color: theme.color.muted, marginBottom: 24 }}>
        Pre-filled from your intake answers where possible. You can update everything here later from My Organisation.
      </div>

      <div style={{ maxHeight: 440, overflowY: "auto", paddingRight: 4, marginBottom: 24 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: theme.color.accent, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 14 }}>Basics</div>
        <Field field="name"   title="Organisation name" />
        <Field field="sector" title="Sector" />

        <div style={fieldWrap}>
          <label style={label}>Approximate headcount</label>
          <select style={{ ...inp }} value={profile.size ?? ""} onChange={(e) => onChange("size", e.target.value)}>
            <option value="">Select…</option>
            {["1–10", "11–50", "51–200", "201–1,000", "1,000+"].map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>

        <div style={{ fontSize: 12, fontWeight: 700, color: theme.color.accent, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 14, marginTop: 8 }}>Key contacts</div>
        <Field field="ceo"       title="Chief Executive Officer" />
        {show(["iso27001", "cyber_essentials", "cyber_essentials_plus", "soc2"]) && <Field field="ciso" title="CISO / Head of Security" />}
        {show(["iso27001", "iso42001"]) && <Field field="dpo" title="Data Protection Officer" />}
        <Field field="itManager" title="IT Manager" />
        {show(["iso42001"]) && <Field field="chiefAiOfficer"  title="Chief AI Officer" />}
        {show(["iso9001"])  && <Field field="qualityManager"  title="Quality Manager" />}

        {(show(["iso27001"]) || show(["iso42001"]) || show(["iso9001"]) || show(["soc2"])) && (
          <>
            <div style={{ fontSize: 12, fontWeight: 700, color: theme.color.accent, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 14, marginTop: 8 }}>Scope</div>
            {show(["iso27001"]) && <Field field="ismsScope"            title="ISMS scope (ISO 27001)"  multiline rows={3} />}
            {show(["iso42001"]) && <Field field="aimsScope"            title="AIMS scope (ISO 42001)"  multiline rows={3} />}
            {show(["iso9001"])  && <Field field="qmsScope"             title="QMS scope (ISO 9001)"    multiline rows={3} />}
            {show(["soc2"])     && <Field field="socServiceDescription" title="In-scope services (SOC 2)" multiline rows={3} />}
          </>
        )}
      </div>

      {error && <div style={{ fontSize: 12, color: theme.color.dangerText, marginBottom: 12 }}>{error}</div>}

      <button
        onClick={onComplete}
        disabled={saving || !profile.name?.trim()}
        style={{
          width: "100%", padding: "11px 18px", borderRadius: 3, border: "none",
          background: !profile.name?.trim() ? theme.color.border : theme.color.accent,
          color: !profile.name?.trim() ? theme.color.muted : "#fff",
          fontSize: 13, fontWeight: 600,
          cursor: saving || !profile.name?.trim() ? "not-allowed" : "pointer",
        }}
      >
        {saving ? "Saving…" : "Complete setup"}
      </button>
    </div>
  );
}

// ── Main wizard ───────────────────────────────────────────────────────────

export function OnboardingPage({ onComplete }: { onComplete: () => void }) {
  const [step, setStep] = useState(0);

  // Step 1
  const [selected,      setSelected]      = useState<Set<FrameworkId>>(new Set());
  const [savingFrameworks, setSavingFrameworks] = useState(false);

  // Step 2
  const [questions, setQuestions] = useState<Question[]>([]);
  const [answers,   setAnswers]   = useState<Record<string, string>>({});
  const saveTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  // Step 3
  const [profile,       setProfile]       = useState<OrgProfile>({ name: "", sector: null, size: null, ceo: null, ciso: null, dpo: null, itManager: null, hrLead: null, chiefAiOfficer: null, qualityManager: null, ismsScope: null, aimsScope: null, qmsScope: null, socServiceDescription: null });
  const [enrolled,      setEnrolled]      = useState<Set<FrameworkId>>(new Set());
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileError,  setProfileError]  = useState("");

  // ── Step 1 handlers ────────────────────────────────────────────────────

  function toggleFramework(id: FrameworkId) {
    setSelected((prev) => {
      // Selecting one level of a scheme (CE / CE Plus, an IASME tier) drops the other.
      const next = new Set(prev.has(id) ? [...prev].filter((x) => x !== id) : withAlternative([...prev], id));
      // Deselecting the last paid standard takes the free reference frameworks with it.
      return new Set(withoutOrphanedReferenceFrameworks([...next]));
    });
  }

  async function handleFrameworksContinue() {
    setSavingFrameworks(true);
    try {
      await api.put("/organizations/frameworks", { frameworkIds: [...selected] });
      setEnrolled(new Set(selected));
      await loadQuestions();
      setStep(1);
    } finally {
      setSavingFrameworks(false);
    }
  }

  // ── Step 2 handlers ────────────────────────────────────────────────────

  const loadQuestions = useCallback(async () => {
    const data = await api.get<{ questions: Question[]; answers: Record<string, { answerText: string }> }>("/questions");
    setQuestions(data.questions);
    setAnswers(Object.fromEntries(Object.entries(data.answers).map(([k, v]) => [k, v.answerText])));
  }, []);

  // Countries and regulations decide which law-specific sets are in scope, so the list is
  // refetched once those answers save; other answers in flight are left untouched.
  const refreshQuestions = useCallback(async () => {
    try {
      const data = await api.get<{ questions: Question[]; answers: Record<string, { answerText: string }> }>("/questions");
      setQuestions(data.questions);
      setAnswers((prev) => {
        const next = { ...prev };
        for (const [k, v] of Object.entries(data.answers)) if (next[k] === undefined) next[k] = v.answerText;
        return next;
      });
    } catch { /* keep the current list */ }
  }, []);

  function handleAnswer(questionId: string, text: string) {
    setAnswers((prev) => ({ ...prev, [questionId]: text }));
    clearTimeout(saveTimers.current[questionId]);
    const changesScope = ["geography", "regulations", "sectorCode"].includes(questions.find((q) => q.id === questionId)?.orgProfileField ?? "");
    saveTimers.current[questionId] = setTimeout(() => {
      api.put(`/questions/answers/${questionId}`, { answerText: text })
        .then(() => { if (changesScope) void refreshQuestions(); })
        .catch(() => {});
    }, 700);
  }

  async function handleQuestionsContinue() {
    // Flush any pending saves before moving on
    Object.values(saveTimers.current).forEach(clearTimeout);
    await Promise.allSettled(
      Object.entries(answers).map(([id, text]) =>
        api.put(`/questions/answers/${id}`, { answerText: text })
      )
    );
    await loadOrgProfile();
    setStep(2);
  }

  // ── Step 3 handlers ────────────────────────────────────────────────────

  const loadOrgProfile = useCallback(async () => {
    const data = await api.get<{ organization: OrgProfile }>("/organizations/profile");
    setProfile(data.organization);
  }, []);

  function handleProfileChange(field: keyof OrgProfile, value: string) {
    setProfile((prev) => ({ ...prev, [field]: value || null }));
  }

  async function handleComplete() {
    if (!profile.name?.trim()) return;
    setProfileError("");
    setSavingProfile(true);
    try {
      await api.patch("/organizations/profile", profile);
      await api.post("/organizations/onboarding/complete", {});
      onComplete();
    } catch {
      setProfileError("Failed to save. Please try again.");
    } finally {
      setSavingProfile(false);
    }
  }

  return (
    <div style={{
      minHeight: "100vh",
      background: theme.color.bg,
      fontFamily: theme.font,
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "center",
      padding: "48px 16px 32px",
    }}>
      <BugReportButton floating />
      <div style={{ width: "100%", maxWidth: 620 }}>
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 36 }}>
          <img src="/abacas-logo.png" alt="Abacas Security" style={{ width: 48, height: 48, objectFit: "contain", marginBottom: 12 }} />
          <div style={{ fontSize: 20, fontWeight: 700, color: theme.color.heading }}>Welcome to Abacas Security</div>
          <div style={{ fontSize: 13, color: theme.color.muted, marginTop: 4 }}>Let's get your compliance programme set up.</div>
        </div>

        {/* Step indicator */}
        <StepBar current={step} />

        {/* Card */}
        <div style={{
          ...card,
          padding: 28,
          boxShadow: "var(--c-sheetShadow)",
        }}>
          {step === 0 && (
            <StepStandards
              selected={selected}
              onToggle={toggleFramework}
              onContinue={handleFrameworksContinue}
              saving={savingFrameworks}
            />
          )}
          {step === 1 && (
            <StepQuestions
              questions={questions}
              answers={answers}
              onAnswer={handleAnswer}
              onContinue={handleQuestionsContinue}
            />
          )}
          {step === 2 && (
            <StepOrgProfile
              profile={profile}
              enrolled={enrolled}
              onChange={handleProfileChange}
              onComplete={handleComplete}
              saving={savingProfile}
              error={profileError}
            />
          )}
        </div>
      </div>
    </div>
  );
}
